import { addDays } from 'date-fns';
import { CalendarDays, X } from 'lucide-react';
import { forwardRef, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { formatDateEntry, isIncompleteDate, isoToDate, isoWithin, maskDateTyping, parseDateEntry } from '@/lib/date-entry';
import { useTranslation } from '@/lib/i18n';
import { cn, toIsoDate, todayIso } from '@/lib/utils';
import { Calendar } from './calendar';
import { controlShell, shellButton, shellInput } from './control-styles';
import { assignRef, RejectionBubble, useRejectedFlash } from './entry-helpers';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from './popover';

export interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  name?: string;
  className?: string;
  autoFocus?: boolean;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-label'?: string;
  onEnter?: (value: string) => void;
  onUnreadable?: (raw: string) => boolean;
}

function rangeMessage(min?: string, max?: string): string {
  if (min && max) return `Pick a date between ${formatDateEntry(min)} and ${formatDateEntry(max)}`;
  if (min) return `Pick a date on or after ${formatDateEntry(min)}`;
  return `Pick a date on or before ${formatDateEntry(max ?? '')}`;
}

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
  {
    value,
    onChange,
    min,
    max,
    required,
    disabled,
    placeholder = 'DD/MM/YYYY',
    id,
    name,
    className,
    autoFocus,
    onEnter,
    onUnreadable,
    ...aria
  },
  ref,
) {
  const common = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(() => formatDateEntry(value));
  const [editing, setEditing] = useState(false);
  const { reason, flash, settle } = useRejectedFlash();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const interactedOutside = useRef(false);

  useEffect(() => {
    if (!editing) setText(formatDateEntry(value));
  }, [value, editing]);

  const accept = (iso: string) => {
    settle();
    setText(formatDateEntry(iso));
    if (iso !== value) onChange(iso);
  };

  const reject = (message: string) => {
    setText(formatDateEntry(value));
    flash(message);
  };

  const commit = (raw: string): string | null => {
    const parsed = parseDateEntry(raw);
    if (parsed === null && onUnreadable?.(raw.trim())) {
      settle();
      setText('');
      return null;
    }
    let problem: string | null = null;
    if (parsed !== null && isIncompleteDate(raw)) problem = 'Type the full date as DD/MM/YYYY';
    else if (parsed === null) problem = `Could not read “${raw.trim()}” as a date`;
    else if (parsed === '' && required) problem = 'A date is required here';
    else if (parsed && !isoWithin(parsed, min, max)) problem = rangeMessage(min, max);
    if (problem || parsed === null) {
      reject(problem ?? '');
      return null;
    }
    accept(parsed);
    return parsed;
  };

  const step = (days: number) => {
    const base = parseDateEntry(text) || value || todayIso();
    const current = isoToDate(base) ?? new Date();
    const next = toIsoDate(addDays(current, days));
    if (isoWithin(next, min, max)) accept(next);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if ((event.altKey && event.key === 'ArrowDown') || event.key === 'F4') {
      event.preventDefault();
      setOpen(true);
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      step(event.key === 'ArrowUp' ? 1 : -1);
    } else if (event.key === 'Enter' && (onEnter || text !== formatDateEntry(value))) {
      event.preventDefault();
      const committed = text === formatDateEntry(value) ? value : commit(text);
      if (committed !== null) onEnter?.(committed);
    } else if (event.key === 'Escape' && text !== formatDateEntry(value)) {
      event.preventDefault();
      setText(formatDateEntry(value));
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div
          ref={shellRef}
          data-disabled={disabled || undefined}
          className={cn(
            controlShell,
            'group/picker flex h-9 items-center',
            reason && 'border-destructive ring-[3px] ring-destructive/20',
            className,
          )}
        >
          <input
            ref={(node) => {
              inputRef.current = node;
              assignRef(ref, node);
            }}
            id={id}
            name={name}
            value={text}
            disabled={disabled}
            required={required}
            autoFocus={autoFocus}
            placeholder={placeholder}
            data-slot="date-input"
            autoComplete="off"
            spellCheck={false}
            aria-label={aria['aria-label']}
            aria-describedby={aria['aria-describedby']}
            aria-invalid={aria['aria-invalid'] || Boolean(reason) || undefined}
            onFocus={() => setEditing(true)}
            onBlur={() => {
              setEditing(false);
              commit(text);
            }}
            onChange={(event) => setText(maskDateTyping(event.target.value, text))}
            onKeyDown={onKeyDown}
            className={shellInput}
          />
          <RejectionBubble reason={reason} anchor={shellRef} onDismiss={settle} />
          {value && !required && !disabled && (
            <button
              type="button"
              tabIndex={-1}
              aria-label={common.t('date.clear')}
              onClick={() => {
                accept('');
                inputRef.current?.focus();
              }}
              className="hidden h-6 w-6 shrink-0 place-items-center rounded text-muted-foreground/70 transition-colors hover:text-foreground group-focus-within/picker:grid group-hover/picker:grid"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label={common.t('date.choose')}
              className={shellButton}
            >
              <CalendarDays className="h-4 w-4" />
            </button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="w-auto p-3"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          interactedOutside.current = false;
        }}
        onInteractOutside={() => {
          interactedOutside.current = true;
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (!interactedOutside.current) inputRef.current?.focus();
        }}
      >
        <Calendar
          value={value}
          min={min}
          max={max}
          autoFocus
          onSelect={(iso) => {
            accept(iso);
            setOpen(false);
          }}
          onClear={
            required
              ? undefined
              : () => {
                  accept('');
                  setOpen(false);
                }
          }
        />
      </PopoverContent>
    </Popover>
  );
});
